import { format, isValid } from "date-fns";
import { toZonedTime } from "date-fns-tz";

interface useDateTimeProps {
  dateFormat?: string;
  dateTimeFormat?: string;
  timeFormat?: string;
}
export function useDateTime({
  dateFormat = "yyyy-MM-dd",
  dateTimeFormat = "yyyy-MM-dd p",
  timeFormat = "p",
}: useDateTimeProps) {
  const formatDate = (
    date: Date | string | null | undefined,
    defaultDisplay: string = "",
    intlFormat?: boolean | null | undefined,
  ) => {
    let displayDate = defaultDisplay;
    if (typeof date === "string") {
      const dateIsValid = isValid(new Date(date));
      if (!intlFormat) {
        displayDate =
          date && dateIsValid
            ? format(new Date(date), dateFormat)
            : defaultDisplay;
      }
      else {
        const options: Intl.DateTimeFormatOptions = {
            year: 'numeric',
            month: 'short', // 'Oct.'
            day: 'numeric'  // '20'
        };
        displayDate = 
          date && dateIsValid
            ? new Intl.DateTimeFormat('en-US', options).format(new Date(date))
            : defaultDisplay;
      }
    } else {
      displayDate = date ? format(date, dateFormat) : defaultDisplay;
    }

    return displayDate;
  };

  const formatDateForInput = (date: string, timezone?: string): string => {
    const zonedDate = formatToLocale(date, timezone);
    const inputFormat = "yyyy-MM-dd";
    return format(zonedDate, inputFormat);
  };

  const formatDateTimeForInput = (date: string, timezone?: string): string => {
    const zonedDate = formatToLocale(date, timezone);
    const inputFormat = "yyyy-MM-dd hh:mm";
    return format(zonedDate, inputFormat);
  };

  const formatDateLocal = (date: string, timezone?: string): string => {
    const zonedDate = formatToLocale(date, timezone);
    return format(zonedDate, dateFormat);
  };

  const formatDateTimeLocal = (date: string, timezone?: string): string => {
    const zonedDate = formatToLocale(date, timezone);
    return format(zonedDate, dateTimeFormat);
  };

  const formatTimeLocal = (date: string, timezone?: string): string => {
    const zonedDate = formatToLocale(date, timezone);
    return format(zonedDate, timeFormat);
  };

  const formatToLocale = (date: string, timezone?: string) => {
    const defaultTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const selectedTimezone = timezone ? timezone : defaultTimezone;
    const utcDate = new Date(date);
    const zonedDate = toZonedTime(utcDate, selectedTimezone);
    return zonedDate;
  };

  return {
    dateFormat,
    dateTimeFormat,
    formatDate,
    formatDateForInput,
    formatDateTimeForInput,
    formatDateTimeLocal,
    formatTimeLocal,
    formatDateLocal,
  };
}
